import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Menu } from '@components/ui/Menu/Menu';
import { CheckIcon, ChevronDownIcon, FileStackIcon } from '@utils/icons';
import styles from './generationSelect.module.css';

export interface IGenerationSelectProps {
  value: number;
  max: number;
  onChange: (gen: number) => void;
}

/**
 * A breakdown "level" (formerly "generation") named by what it shows, matching
 * the scheduler's own hierarchy: level 1 = Groups (the ScheduleGroup rows), level
 * 2 = Tasks (top-level tasks), level 3+ = Sub-tasks by `parentId` depth. Sub-task
 * tiers are numbered RELATIVE to the first sub-level (LV1, LV2, …) so the depth
 * reads naturally rather than as an absolute generation number.
 */
function levelLabel(n: number, t: TFunction): string {
  if (n <= 1) return t('activities.tracker.level.groups');
  if (n === 2) return t('activities.tracker.level.tasks');
  return t('activities.tracker.level.subtasksN', { n: n - 2 });
}

export function GenerationSelect({ value, max, onChange }: IGenerationSelectProps) {
  const { t } = useTranslation();
  const levels = Array.from({ length: max }, (_, i) => i + 1);
  return (
    <Menu.Root>
      <Menu.Trigger className={styles.trigger}>
        <span className={styles.icon} aria-hidden>
          <FileStackIcon size={14} />
        </span>
        {levelLabel(value, t)}
        <span className={styles.chevron} aria-hidden>
          <ChevronDownIcon size={14} />
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner>
          <Menu.Popup>
            {levels.map((g) => (
              <Menu.Item key={g} onClick={() => onChange(g)} className={styles.item}>
                {levelLabel(g, t)}
                {g === value && <CheckIcon size={14} />}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
